import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import DashboardStyle from "../../app/dashboard/Styles/Dashboard.module.css";
import PageLayout from "../../app/dashboard/Components/Layout/PageLayout";
import {
  FormInput,
  FormsSelect,
  FormTemplate,
} from "../dashboard/Components/Forms/InputTemplate";
import FormUploadComponent from "../dashboard/Components/Forms/FormUploadComponent";
import {
  TableActionsDownload,
  TableActionsDownloadNew,
} from "../dashboard/Components/Misc/ActionsModVersion";
import { removeListData } from "../../utils/functions/ResourceFunctions";
import { CreateApplicant } from "../../utils/redux/HR/HRSlice";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router";
import { useDispatch } from "react-redux";
import Table from "../dashboard/Components/Table/Table";
import { ActionButtons } from "../global/components/Buttons/buttons";

function JobApplicationForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const pathArray = window.location.pathname.split("/");
  const jobIdIndex =
    pathArray.findIndex((item) => item === "job-application") + 1;
  const jobId = pathArray[jobIdIndex];

  const [uploadFile, setUploadFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const formMethods = useForm({ mode: "all" });

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = formMethods;

  const documentList = [
    { name: "Resume", value: 331 },
    { name: "Cover Letter", value: 332 },
    { name: "Certificate", value: 330 },
  ];

  const submit = async (data) => {
    const formData = new FormData();

    // Append form data based on the input fields
    formData.append("JobPostingId", jobId);
    formData.append("SurName", data.lastName);
    formData.append("FirstName", data.firstName);
    formData.append("OtherName", data.otherNames || "");
    formData.append("Gender", data.gender);
    formData.append("MaritalStatus", data.maritalStatus);
    formData.append("Email", data.email);
    formData.append("Phone", data.phone);
    formData.append("DOB", data.dob);
    formData.append("Location", data.location);
    formData.append("Portfolio", data.website);

    documents.forEach((document, index) => {
      formData.append(`Documents[${index}].id`, document.fileName);
      formData.append(`Documents[${index}].definitionId`, document.fileName);
      formData.append(
        `Documents[${index}].file`,
        document.file,
        document.file?.name
      );
    });

    try {
      setLoading(true);
      const res = await dispatch(CreateApplicant(formData));
      if (res.payload.successful) {
        toast.success(res.payload.statusMessage);
        reset();
        navigate("/");
        setLoading(false);
      } else {
        toast.error(res.payload.statusMessage);
        setLoading(false);
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("An error occurred. Please try again");
      setLoading(false);
    }
  };

  console.log("Documents", documents);

  return (
    <div className="max-w-7xl mx-auto my-10 p-6">
      <PageLayout title="Job Application" hasBack={true}>
        <FormProvider {...formMethods}>
          <FormTemplate handleSubmit={handleSubmit(submit)}>
            {/* Basic Information Section */}
            <section className={DashboardStyle.form_section}>
              <h4 className={DashboardStyle.form_section_title}>
                Basic Information
              </h4>
              <div className={DashboardStyle.inputs_group}>
                <div>
                  <FormInput
                    title="First Name"
                    camelCase="firstName"
                    placeholder="Oluwasegun"
                    required
                  />
                  <FormInput
                    title="Last Name"
                    camelCase="lastName"
                    placeholder="Olaniyan"
                    required
                  />
                </div>
                <div>
                  <FormInput
                    title="Email"
                    camelCase="email"
                    placeholder="example@email.com"
                    required
                  />
                  <FormInput
                    title="Other Names"
                    camelCase="otherNames"
                    placeholder="Default"
                  />
                </div>
                <div>
                  <FormsSelect
                    title="Work Mode"
                    camelCase="workMode"
                    placeholder="Select Work Mode"
                    array={[
                      <option key="hybrid" value="Hybrid">
                        Hybrid
                      </option>,
                      <option key="remote" value="Remote">
                        Remote
                      </option>,
                      <option key="onsite" value="Onsite">
                        Onsite
                      </option>,
                    ]}
                    required
                  />
                  <FormsSelect
                    title="Marital Status"
                    camelCase="maritalStatus"
                    placeholder="Default"
                    array={[
                      <option key="single" value="Single">
                        Single
                      </option>,
                      <option key="married" value="Married">
                        Married
                      </option>,
                    ]}
                  />
                </div>
                <div>
                  <FormsSelect
                    title="Gender"
                    camelCase="gender"
                    placeholder="Select Gender"
                    array={[
                      <option key="male" value="Male">
                        Male
                      </option>,
                      <option key="female" value="Female">
                        Female
                      </option>,
                    ]}
                    required
                  />
                  <FormInput
                    title="Phone"
                    camelCase="phone"
                    placeholder="Default"
                    required
                  />
                </div>
                <div>
                  <FormInput
                    title="Location"
                    camelCase="location"
                    placeholder="Default"
                    required
                  />
                  <FormInput
                    title="DOB"
                    camelCase="dob"
                    placeholder="Placeholder"
                    type="date"
                  />
                </div>
                <div>
                  <FormInput
                    title="Website"
                    camelCase="website"
                    placeholder="Placeholder"
                  />
                </div>
              </div>
            </section>

            {/* Upload Supporting Documents Section */}
            <section className={DashboardStyle.form_section}>
              <h4 className={DashboardStyle.form_section_title}>
                Upload Supporting Documents
              </h4>
              <FormUploadComponent
                documentList={documentList}
                uploadFile={uploadFile}
                setUploadFile={setUploadFile}
                setVendorDocument={setDocuments}
                vendorDocument={documents}
                isOptional={false}
              />
            </section>

            {/* Uploaded Documents Section */}
            <section className={DashboardStyle.form_section}>
              <h4 className={DashboardStyle.form_section_title}>
                Documents Uploads
              </h4>
              <div
                className={`${DashboardStyle.labels_group} ${DashboardStyle.labels_group_downloads}`}
              >
                {documents.length === 0 ? (
                  <p>No Record Available</p>
                ) : (
                  <Table className={DashboardStyle.table}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>File Name</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc, index) => (
                        <tr key={doc.id || index}>
                          <td>{index + 1}</td>
                          <td>{doc.value}</td>
                          <td>
                            <TableActionsDownloadNew
                              file={doc.file || null}
                              url={doc.fileUrl || null}
                              actions={() =>
                                removeListData({
                                  value: index,
                                  list: documents,
                                  setList: setDocuments,
                                })
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </section>

            {/* Form Actions */}
            <div className={DashboardStyle.button_cage}>
              {/* <button
                type="button"
                onClick={() => reset()}
                className="px-6 py-2 bg-gray-100 text-gray-800 border border-gray-300 rounded-md"
              >
                Save as Draft
              </button> */}
              <ActionButtons
                type="submit"
                disabled={loading || !isValid || documents.length === 0}
                className="px-6 py-2 bg-green-600 text-white rounded-md"
              >
                {loading ? "Please Wait..." : "Submit"}
              </ActionButtons>
            </div>
          </FormTemplate>
        </FormProvider>
      </PageLayout>
    </div>
  );
}

export default JobApplicationForm;
